import {useEffect, useMemo} from "react";
import type {Theme} from "@glideapps/glide-data-grid";
import {getOtsThemeVariables, type OtsColorScheme} from "../theme/otsThemeTokens";

export type DataGridFocusResult = {firstRow?: number; count: number};

/** Resolve records through the renderer's display mapping, after sorting and separator insertion. */
export function getFocusedDisplayRows<T>(
  rowCount: number,
  recordAt: (row: number) => T | undefined,
  matches: (record: T) => boolean
): number[] {
  const result: number[] = [];
  for (let row = 0; row < rowCount; row++) {
    const record = recordAt(row);
    if (record && matches(record)) result.push(row);
  }
  return result;
}

export function useDataGridRowFocus<T>({
  rowCount,
  recordAt,
  matches,
  focusKey,
  onResolved,
}: {
  rowCount: number;
  recordAt: (row: number) => T | undefined;
  matches: (record: T) => boolean;
  focusKey?: string;
  onResolved?: (result: DataGridFocusResult) => void;
}) {
  const rows = useMemo(
    () => (focusKey ? getFocusedDisplayRows(rowCount, recordAt, matches) : []),
    [rowCount, recordAt, matches, focusKey]
  );
  useEffect(() => {
    if (focusKey) onResolved?.({firstRow: rows[0], count: rows.length});
  }, [rows, focusKey, onResolved]);
  return useMemo(() => new Set(rows), [rows]);
}

export function getDataGridFocusTheme(scheme: OtsColorScheme): Partial<Theme> {
  const colors = getOtsThemeVariables("dispatch-go", scheme);
  return {
    bgCell: colors["--ots-blue-container"],
    bgCellMedium: colors["--ots-blue-container"],
    textDark: colors["--ots-on-blue-container"],
    textMedium: colors["--ots-on-blue-container"],
  };
}
